import type {ComponentType} from 'react';
import type {z} from 'zod';

export type SuccessData = {
  success: true;
  referenceId?: string;
};

export type ErrorCode =
  | 'VALIDATION_ERROR'
  | 'RECAPTCHA_FAILED'
  | 'UPSTREAM_ERROR'
  | 'UNKNOWN_FORM_ERROR'
  | 'UNKNOWN_ERROR';

export type Errors = {
  formErrors: string[];
  fieldErrors: {
    [key: string]: string[];
  };
};

export type ErrorData = {
  code: ErrorCode;
  message: string;
  errors?: Errors;
};

export type FormDataKey = string;
export type FormDataValue =
  | FormDataEntryValue
  | FormDataEntryValue[]
  | FileList
  | null
  | undefined;
export type FormSubmission = Record<FormDataKey, FormDataValue>;

export class ApiError extends Error {
  public data: ErrorData;

  constructor(message: string, data: ErrorData, options?: ErrorOptions) {
    super(message, options);
    this.name = 'ApiError';
    this.data = data;
  }
}

export type SaveMethod = 'SFDC_CASE' | 'SFDC_LEAD' | 'GOOGLE_SHEET' | 'NONE';
export type FieldMap = Record<string, string>;

/**
 * Unwraps Zod schema refinements (e.g. .refine()) to extract the underlying
 * object shape type. Needed because calling .refine() wraps ZodObject and
 * removes top-level .shape property access.
 */
export type GetSchemaShape<T> = T extends {shape: infer S}
  ? S
  : T extends {_def: {schema: infer Inner}}
    ? GetSchemaShape<Inner>
    : T extends {_def: {innerType: infer Inner}}
      ? GetSchemaShape<Inner>
      : Record<string, z.ZodTypeAny>;

export type SaveConfig<TField extends string = string> =
  | {
      method: 'SFDC_CASE' | 'SFDC_LEAD';
      fieldMap: Partial<Record<TField, string>>;
      serializedDataField?: string;
    }
  | {
      method: 'GOOGLE_SHEET';
      spreadsheetId: string;
      fields: TField[];
    }
  | {
      method: 'NONE';
    };

export interface ServerRegistryEntry<TField extends string = string> {
  recaptchaThreshold?: number; // uses default threshold if omitted
  save: SaveConfig<TField>;
}

type FormFields<TEntry> = TEntry extends {schema: infer TSchema}
  ? keyof GetSchemaShape<TSchema> & string
  : string;

export type ServerRegistry<TRegistry extends Record<string, RegistryEntry>> = {
  [K in keyof TRegistry]: ServerRegistryEntry<FormFields<TRegistry[K]>>;
};

export interface RegistryEntry<TSchema extends z.ZodTypeAny = z.ZodTypeAny> {
  id: string;
  slug: string;
  component: ComponentType;
  title: string;
  description: string;
  includeInSitemap: boolean;
  schema: TSchema;
  recaptchaAction: string;
}

export type InferFormValues<T> =
  T extends RegistryEntry<infer TSchema> ? z.infer<TSchema> : never;
